// src/components/founderDashboard/hq.tsx
// Burst HQ: the shared shell, tokens and building blocks for the founder
// dashboard pages (Usage, Insights, Growth). Follows the Oct 2026 "1a Console"
// handoff: dark sidebar, one KPI strip per page, identical white cards, and
// chart colours on the Burst palette.
import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";

// ---------- tokens ----------
export const C = {
    cream: "#F2F1ED",
    ink: "#161925",
    sage: "#909B7B",
    sageD: "#616B4F",
    sageL: "#C9CEBE",
    sageL2: "#D4D8CA",
    alert: "#A9472F",
    watch: "#DAD7CE",
    neutral: "#C9C6BC",
    panel: "#EDEBE6",
    inset: "#F7F6F3",
    white: "#FFFFFF",
    slate: "#2F4858",
    rust: "#CA6B45",
    gold: "#F2D28E",
    mauve: "#A796A0",
    sky: "#C2D7EA",
    brown: "#947D6F",
    other: "#8B9DAF",
    sand: "#EFE8D6",
    muted: "rgba(22,25,37,0.55)",
    faint: "rgba(22,25,37,0.45)",
    tick: "rgba(22,25,37,0.5)",
    hair: "rgba(22,25,37,0.08)",
    rowLine: "rgba(22,25,37,0.07)",
    grid: "rgba(22,25,37,0.06)",
    base: "rgba(22,25,37,0.25)",
    track: "rgba(22,25,37,0.06)",
};
export const FONT = "Archivo, sans-serif";
export const FONT_NUM = "'Barlow Condensed', sans-serif";

// Workout types only. Strength and Gym are the same discipline.
export const DISC: Record<string, string> = {
    Running: C.gold, Gym: C.mauve, Strength: C.mauve, Hiit: C.brown, HIIT: C.brown,
    Mobility: C.sky, Hyrox: C.rust, Erg: C.slate, Rowing: C.slate, Other: C.other,
    "Watch-pushed": C.watch,
};
export const discColor = (t: string) => DISC[t] || C.other;
export const ACCT: Record<string, string> = { Burst: C.slate, Personal: C.sage };
export const NGROUP: Record<string, string> = {
    "Track Jack": C.slate, "Planning nudges": C.gold, "Watch sync": C.sage,
    "Workout reminders": C.mauve, Social: C.sky, Other: C.other,
};
export const ngroupColor = (g: string) => NGROUP[g] || C.other;

export const microLabel: React.CSSProperties = {
    fontFamily: FONT, fontWeight: 600, fontSize: 10, letterSpacing: "0.12em",
    textTransform: "uppercase", color: C.muted,
};
export const numStyle = (size = 15, weight = 600): React.CSSProperties => ({
    fontFamily: FONT_NUM, fontWeight: weight, fontSize: size, fontVariantNumeric: "tabular-nums",
});

// ---------- parsing and formatting ----------
export const num = (v: any): number | null => {
    if (v === null || v === undefined || v === "") return null;
    const n = typeof v === "number" ? v : parseFloat(String(v).replace(/,/g, ""));
    return Number.isFinite(n) ? n : null;
};
// sheet dates arrive as "dd/mm/yyyy" (imported history) or "yyyy-mm-dd" (cron)
export const parseDate = (v: any): Date | null => {
    if (!v && v !== 0) return null;
    const s = String(v).trim();
    let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
    m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (m) return new Date(Date.UTC(+m[3], +m[2] - 1, +m[1]));
    return null;
};
export const dayKey = (d: Date) => d.toISOString().slice(0, 10);
export const median = (xs: number[]) => {
    if (!xs.length) return 0;
    const s = [...xs].sort((a, b) => a - b);
    const mid = Math.floor(s.length / 2);
    return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
};
export const fmt = (n: number | null | undefined, dp = 0) =>
    n === null || n === undefined || !Number.isFinite(n) ? "-" : n.toLocaleString("en-GB", { maximumFractionDigits: dp, minimumFractionDigits: 0 });

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
// "2026-09-14" -> "14 Sep"
export const shortDate = (v: any) => {
    const d = parseDate(v);
    return d ? `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}` : String(v ?? "");
};
// Month labels get a year only when the series crosses one.
export const monthFormatter = (keys: string[]) => {
    const years = new Set(keys.map(k => String(k).slice(0, 4)));
    return (v: any) => {
        const d = parseDate(String(v).length === 7 ? `${v}-01` : v);
        if (!d) return String(v ?? "");
        return years.size > 1 ? `${MONTHS[d.getUTCMonth()]} ${String(d.getUTCFullYear()).slice(2)}` : MONTHS[d.getUTCMonth()];
    };
};

// ---------- date range ----------
export type RangeSel = { kind: "preset"; days: number } | { kind: "all" } | { kind: "since" } | { kind: "custom"; start: string; end: string };

// App Store go-live date - anchors the "Since live" range
export const LIVE_DATE = "2025-11-28";

const ddmm = (iso: string) => {
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
};
export const rangeLabel = (r: RangeSel) =>
    r.kind === "all" ? "All time"
        : r.kind === "since" ? "Since live"
            : r.kind === "preset" ? `Last ${r.days} days`
                : `${ddmm(r.start)} - ${ddmm(r.end)}`;

// The brand segmented toggle with the page's quick presets, plus a "More"
// segment that opens every other option: the full preset list, Since live,
// All time and a custom start/end. Whatever is chosen there shows as the
// label of that segment.
export const RangePicker: React.FC<{ range: RangeSel; onChange: (r: RangeSel) => void; presets: number[] }> =
    ({ range, onChange, presets }) => {
        const [open, setOpen] = useState(false);
        const [cs, setCs] = useState(range.kind === "custom" ? range.start : "");
        const [ce, setCe] = useState(range.kind === "custom" ? range.end : "");
        const isQuick = range.kind === "preset" && presets.includes(range.days);
        const pick = (r: RangeSel) => { onChange(r); setOpen(false); };
        const seg = (on: boolean): React.CSSProperties => ({
            border: "none", cursor: "pointer", borderRadius: 10, fontFamily: FONT, fontSize: 13,
            fontWeight: on ? 700 : 600, background: on ? C.sage : "transparent",
            color: on ? "#FFF" : "rgba(22,25,37,0.6)", whiteSpace: "nowrap",
        });
        const row = (on: boolean): React.CSSProperties => ({
            display: "block", width: "100%", textAlign: "left", padding: "8px 10px",
            borderRadius: 8, border: "none", cursor: "pointer", fontSize: 13, fontFamily: FONT,
            fontWeight: on ? 700 : 500, background: on ? C.sage : "transparent", color: on ? "#FFF" : C.ink,
        });
        const dateInput: React.CSSProperties = {
            flex: 1, minWidth: 0, padding: "7px 8px", borderRadius: 10,
            border: "1px solid rgba(22,25,37,0.2)", fontFamily: FONT, fontSize: 12, background: "#FFF", color: C.ink,
        };
        const bad = !cs || !ce || cs > ce;
        return (
            <div style={{ position: "relative" }}>
                <div style={{ display: "flex", background: "#FFF", border: `1.5px solid ${C.ink}`, borderRadius: 14, padding: 4, gap: 4 }}>
                    {presets.map(d => (
                        <button key={d} className="hq-seg" style={seg(range.kind === "preset" && range.days === d)}
                            onClick={() => pick({ kind: "preset", days: d })}>{d} days</button>
                    ))}
                    <button className="hq-seg" style={seg(!isQuick)} onClick={() => setOpen(o => !o)}>
                        {isQuick ? "More" : rangeLabel(range)} {"▾"}
                    </button>
                </div>
                {open && (<>
                    <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
                    <div style={{
                        position: "absolute", top: "calc(100% + 8px)", right: 0, zIndex: 50,
                        width: 280, maxWidth: "calc(100vw - 32px)", background: "#FFF", borderRadius: 14,
                        border: `1px solid ${C.hair}`, boxShadow: "0 12px 32px rgba(22,25,37,0.14)",
                        padding: 10, fontFamily: FONT,
                    }}>
                        {[7, 30, 90, 180].map(d => (
                            <button key={d} style={row(range.kind === "preset" && range.days === d)}
                                onClick={() => pick({ kind: "preset", days: d })}>Last {d} days</button>
                        ))}
                        <button style={row(range.kind === "since")} onClick={() => pick({ kind: "since" })}>
                            Since live ({ddmm(LIVE_DATE)})
                        </button>
                        <button style={row(range.kind === "all")} onClick={() => pick({ kind: "all" })}>All time</button>
                        <div style={{ borderTop: `1px solid ${C.hair}`, margin: "8px 0 10px" }} />
                        <div style={{ ...microLabel, padding: "0 2px" }}>Custom range</div>
                        <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                            <input type="date" value={cs} onChange={e => setCs(e.target.value)} style={dateInput} />
                            <input type="date" value={ce} onChange={e => setCe(e.target.value)} style={dateInput} />
                        </div>
                        <button disabled={bad} onClick={() => pick({ kind: "custom", start: cs, end: ce })} style={{
                            width: "100%", marginTop: 8, padding: "9px 0", borderRadius: 999, border: "none",
                            background: bad ? "rgba(22,25,37,0.15)" : C.sage, color: "#FFF",
                            fontWeight: 700, fontSize: 13, fontFamily: FONT, cursor: bad ? "default" : "pointer",
                        }}>Apply</button>
                    </div>
                </>)}
            </div>
        );
    };

// ---------- shell ----------
const SHELL_CSS = `
html{scroll-behavior:smooth}
.hq-root{display:grid;grid-template-columns:236px minmax(0,1fr);min-height:100vh;background:${C.cream};font-family:${FONT};color:${C.ink};-webkit-font-smoothing:antialiased}
.hq-root *{box-sizing:border-box}
.hq-side{background:${C.ink};color:${C.cream};padding:28px 18px;display:flex;flex-direction:column;gap:28px;position:sticky;top:0;height:100vh}
.hq-brand{display:flex;flex-direction:column;gap:4px;padding:0 10px}
.hq-nav{display:flex;flex-direction:column;gap:4px}
.hq-group{font:600 10px ${FONT};letter-spacing:.14em;text-transform:uppercase;color:rgba(242,241,237,.45);padding:0 10px 6px}
.hq-link{display:flex;justify-content:space-between;align-items:center;padding:10px;border-radius:10px;color:rgba(242,241,237,.8);font:500 14px ${FONT};text-decoration:none}
.hq-link:hover{background:rgba(242,241,237,.08);color:${C.cream}}
.hq-link.on,.hq-link.on:hover{background:${C.sage};color:#fff;font-weight:700}
.hq-foot{margin-top:auto;padding:14px 10px 0;border-top:1px solid rgba(242,241,237,.12);font:400 12px/1.5 ${FONT};color:rgba(242,241,237,.55)}
.hq-main{padding:32px 40px 64px;max-width:1520px;width:100%;display:flex;flex-direction:column;gap:24px;min-width:0}
.hq-card{background:#fff;border-radius:18px;box-shadow:0 8px 24px rgba(22,25,37,.06);padding:22px 24px;min-width:0;display:flex;flex-direction:column;gap:14px}
.hq-row:hover td{background:${C.inset}}
.hq-chip{display:flex;gap:6px;align-items:baseline;padding:7px 14px;border-radius:999px;font:600 13px ${FONT};color:${C.ink};text-decoration:none;white-space:nowrap}
.hq-chip:hover{background:#fff}
.hq-secnav{position:sticky;top:0;z-index:5;background:${C.cream};padding:10px 0;margin:-6px 0;display:flex;flex-wrap:wrap;gap:6px;border-bottom:1px solid rgba(22,25,37,.1)}
.hq-section{scroll-margin-top:64px}
.hq-seg{padding:8px 16px}
.hq-split{display:grid;grid-template-columns:minmax(0,1.7fr) minmax(0,1fr);gap:20px}
.hq-split.r{grid-template-columns:minmax(0,1fr) minmax(0,1.7fr)}
@media (max-width:1180px){.hq-split,.hq-split.r{grid-template-columns:minmax(0,1fr)}}
.hq-root .recharts-text{font-family:${FONT}}
.hq-root .recharts-tooltip-wrapper{z-index:10}
@media (max-width:900px){
  .hq-root{grid-template-columns:minmax(0,1fr)}
  .hq-side{position:static;height:auto;padding:12px 16px;flex-direction:row;align-items:center;gap:18px;overflow-x:auto}
  .hq-brand{padding:0;flex-shrink:0}
  .hq-brand-eyebrow,.hq-group,.hq-foot{display:none}
  .hq-nav{flex-direction:row;gap:4px}
  .hq-link{padding:8px 12px;white-space:nowrap}
  .hq-main{padding:20px 16px 48px;gap:18px}
  .hq-card{padding:18px 16px}
  .hq-secnav{flex-wrap:nowrap;overflow-x:auto}
  .hq-secnav,.hq-side{scrollbar-width:none}
  .hq-secnav::-webkit-scrollbar,.hq-side::-webkit-scrollbar{display:none}
  .hq-h1{font-size:26px!important}
  .hq-seg{padding:7px 10px;font-size:12px!important}
}
`;

// [label, path, badge?]
const NAV: [string, [string, string, string?][]][] = [
    ["Daily", [["Brief", "/founder/today", "Today"]]],
    ["Analytics", [["Usage", "/founder/usage"], ["Insights", "/founder/insights"], ["Growth", "/founder/growth"]]],
    ["Commercial", [["Pipeline", "/founder/pipeline"]]],
];

export const Shell: React.FC<{ title: string; eyebrow?: string; right?: React.ReactNode; titleAside?: React.ReactNode; titleSize?: number; children: React.ReactNode }> =
    ({ title, eyebrow = "Analytics", right, titleAside, titleSize = 32, children }) => {
    const { pathname } = useLocation();
    return (
        <div className="hq-root">
            <style>{SHELL_CSS}</style>
            <aside className="hq-side">
                <div className="hq-brand">
                    <div className="hq-brand-eyebrow" style={{ ...microLabel, letterSpacing: "0.16em", color: "rgba(242,241,237,.55)" }}>Burst Athletics</div>
                    <div style={{ fontFamily: FONT, fontWeight: 800, fontSize: 22, letterSpacing: "-0.01em" }}>Burst HQ</div>
                </div>
                <nav className="hq-nav">
                    {NAV.map(([group, links]) => (
                        <React.Fragment key={group}>
                            <div className="hq-group" style={group === NAV[0][0] ? undefined : { marginTop: 14 }}>{group}</div>
                            {links.map(([label, to, badge]) => (
                                <Link key={to} to={to} className={`hq-link${pathname.startsWith(to) ? " on" : ""}`}>
                                    {label}
                                    {badge && <span style={{ font: `600 11px ${FONT}`, background: "rgba(242,241,237,.2)", color: "#fff", padding: "3px 8px", borderRadius: 999 }}>{badge}</span>}
                                </Link>
                            ))}
                        </React.Fragment>
                    ))}
                </nav>
                <div className="hq-foot">Real activity tracking since 19 Aug 2026</div>
            </aside>
            <main className="hq-main">
                <header style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: "14px 20px", flexWrap: "wrap" }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: titleAside ? 6 : 4 }}>
                        <div style={{ ...microLabel, fontSize: 11 }}>{eyebrow}</div>
                        <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
                            <h1 className="hq-h1" style={{ margin: 0, fontFamily: FONT, fontWeight: 800, fontSize: titleSize, lineHeight: titleAside ? 1 : undefined, letterSpacing: titleSize > 32 ? "-0.025em" : "-0.02em", color: C.ink }}>{title}</h1>
                            {titleAside}
                        </div>
                    </div>
                    {right}
                </header>
                {children}
            </main>
        </div>
    );
};

// ---------- KPI strip ----------
export type Kpi = { label: string; value: string; sub?: React.ReactNode; color?: string };
// Dividers are inset shadows clipped by the strip, so they stay right
// whatever the column count after wrapping.
export const KpiStrip: React.FC<{ items: Kpi[]; min?: number; size?: number }> = ({ items, min = 150, size = 36 }) => (
    <div style={{
        display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`,
        background: "#FFF", borderRadius: 18, boxShadow: "0 8px 24px rgba(22,25,37,.06)", overflow: "hidden",
    }}>
        {items.map(k => (
            <div key={k.label} style={{
                padding: "18px 20px 16px", display: "flex", flexDirection: "column", gap: 6, minWidth: 0,
                boxShadow: `-1px 0 0 ${C.hair}, 0 -1px 0 ${C.hair}`,
            }}>
                <div style={microLabel}>{k.label}</div>
                <div style={{ ...numStyle(size), lineHeight: 1, color: k.color || C.ink, whiteSpace: "nowrap" }}>{k.value}</div>
                {k.sub !== undefined && <div style={{ fontFamily: FONT, fontSize: 12, color: C.muted }}>{k.sub}</div>}
            </div>
        ))}
    </div>
);

// ---------- cards and layout ----------
export const Card: React.FC<{ title?: React.ReactNode; right?: React.ReactNode; children: React.ReactNode; span?: boolean; gap?: number }> =
    ({ title, right, children, span, gap }) => (
        <div className="hq-card" style={{ gridColumn: span ? "1 / -1" : undefined, gap: gap ?? 14 }}>
            {(title || right) && (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px 16px", flexWrap: "wrap" }}>
                    {title && <div style={{ fontFamily: FONT, fontWeight: 700, fontSize: 15, color: C.ink }}>{title}</div>}
                    {right && <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>{right}</div>}
                </div>
            )}
            {children}
        </div>
    );

export const Grid: React.FC<{ min?: number; children: React.ReactNode }> = ({ min = 300, children }) => (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${min}px), 1fr))`, gap: 20 }}>{children}</div>
);

// A wide card beside a narrow one; stacks below ~1180px.
export const Split: React.FC<{ children: React.ReactNode; narrowFirst?: boolean }> = ({ children, narrowFirst }) =>
    <div className={`hq-split${narrowFirst ? " r" : ""}`}>{children}</div>;

export const SectionHead: React.FC<{ id: string; n: string; label: string; title: string; right?: React.ReactNode; intro?: React.ReactNode }> =
    ({ id, n, label, title, right, intro }) => (
        <div id={id} className="hq-section" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: "10px 20px", flexWrap: "wrap", paddingTop: 16 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <div style={{ ...microLabel, fontSize: 11, color: C.sageD }}>{n} · {label}</div>
                <h2 style={{ margin: 0, fontFamily: FONT, fontWeight: 800, fontSize: 22, letterSpacing: "-0.01em", color: C.ink }}>{title}</h2>
                {intro && <div style={{ fontFamily: FONT, fontSize: 13, lineHeight: 1.5, color: "rgba(22,25,37,.6)", maxWidth: 900, marginTop: 2 }}>{intro}</div>}
            </div>
            {right}
        </div>
    );

export const SectionNav: React.FC<{ items: { id: string; n: string; label: string }[] }> = ({ items }) => (
    <nav className="hq-secnav">
        {items.map(s => (
            <a key={s.id} href={`#${s.id}`} className="hq-chip">
                <span style={{ ...numStyle(14), color: C.sageD }}>{s.n}</span>{s.label}
            </a>
        ))}
    </nav>
);

// ---------- legends ----------
export type LegendItem = { label: string; color: string; kind?: "sq" | "line" | "dot" | "dash" | "dotted" | "vline" };
const Swatch: React.FC<{ color: string; kind: LegendItem["kind"] }> = ({ color, kind }) => {
    switch (kind) {
        case "line": return <span style={{ width: 14, height: 2, background: color, borderRadius: 1 }} />;
        case "dot": return <span style={{ width: 10, height: 10, borderRadius: "50%", background: color }} />;
        case "dash": return <span style={{ width: 14, borderTop: `2px dashed ${color}` }} />;
        case "dotted": return <span style={{ width: 14, borderTop: `2px dotted ${color}` }} />;
        case "vline": return <span style={{ width: 2, height: 12, background: color }} />;
        default: return <span style={{ width: 10, height: 10, borderRadius: 3, background: color }} />;
    }
};
export const Legend: React.FC<{ items: LegendItem[] }> = ({ items }) => (
    <div style={{ display: "flex", gap: "6px 16px", flexWrap: "wrap" }}>
        {items.map(i => (
            <span key={i.label} style={{ display: "flex", gap: 6, alignItems: "center", fontFamily: FONT, fontWeight: 500, fontSize: 12, color: "rgba(22,25,37,.7)", whiteSpace: "nowrap" }}>
                <Swatch color={i.color} kind={i.kind} />{i.label}
            </span>
        ))}
    </div>
);

// ---------- bar lists ----------
// Label and value on one line, a thin bar underneath, an optional note.
export const StatBar: React.FC<{ label: React.ReactNode; value: string; count?: string; pct: number; color?: string; note?: React.ReactNode; height?: number; valueSize?: number }> =
    ({ label, value, count, pct, color = C.sage, note, height = 6, valueSize = 20 }) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
                <div style={{ fontFamily: FONT, fontWeight: 500, fontSize: 13, lineHeight: 1.35 }}>{label}</div>
                <div style={{ display: "flex", gap: 6, alignItems: "baseline", whiteSpace: "nowrap" }}>
                    <span style={{ ...numStyle(valueSize), lineHeight: 1 }}>{value}</span>
                    {count && <span style={{ fontFamily: FONT, fontSize: 11, color: C.tick }}>{count}</span>}
                </div>
            </div>
            <div style={{ height, borderRadius: height, background: C.hair }}>
                <div style={{ height, borderRadius: height, background: color, width: `${Math.max(0, Math.min(100, pct))}%` }} />
            </div>
            {note && <div style={{ fontFamily: FONT, fontSize: 11, lineHeight: 1.4, color: C.tick }}>{note}</div>}
        </div>
    );

// Label | bar | value on one row.
export const BarRow: React.FC<{ label: React.ReactNode; value: string; pct: number; color?: string; labelWidth?: number; valueWidth?: number; height?: number; dot?: string }> =
    ({ label, value, pct, color = C.sage, labelWidth = 90, valueWidth = 44, height = 12, dot }) => (
        <div style={{ display: "grid", gridTemplateColumns: `${dot ? "16px " : ""}${labelWidth}px minmax(0,1fr) ${valueWidth}px`, gap: 10, alignItems: "center" }}>
            {dot && <span style={{ width: 12, height: 12, borderRadius: "50%", background: dot }} />}
            <div style={{ fontFamily: FONT, fontWeight: 500, fontSize: 12.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={typeof label === "string" ? label : undefined}>{label}</div>
            <div style={{ height, borderRadius: 4, background: "rgba(22,25,37,.05)" }}>
                <div style={{ height, borderRadius: 4, background: color, width: `${Math.max(0, Math.min(100, pct))}%`, minWidth: pct > 0 ? 2 : 0 }} />
            </div>
            <div style={{ ...numStyle(15), textAlign: "right" }}>{value}</div>
        </div>
    );

export const Empty: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div style={{ background: C.inset, borderRadius: 12, padding: "18px 16px", color: C.muted, fontSize: 13, fontFamily: FONT }}>{children}</div>
);

export const Panel: React.FC<{ label: string; value: string; color?: string; sub?: React.ReactNode }> = ({ label, value, color, sub }) => (
    <div style={{ background: C.panel, borderRadius: 14, padding: "14px 16px", minWidth: 0 }}>
        <div style={microLabel}>{label}</div>
        <div style={{ ...numStyle(32), lineHeight: 1.1, color: color || C.ink }}>{value}</div>
        {sub && <div style={{ fontFamily: FONT, fontSize: 11, color: C.muted, marginTop: 2 }}>{sub}</div>}
    </div>
);

// Small inline toggle (filters, chart scale).
export const Seg: React.FC<{ options: string[]; value: string; onChange: (v: string) => void; mini?: boolean }> =
    ({ options, value, onChange, mini }) => (
        <div style={{
            display: "inline-flex", gap: 2, padding: 3,
            background: mini ? C.cream : "#FFF", border: mini ? "none" : "1px solid rgba(22,25,37,.15)",
            borderRadius: mini ? 10 : 12,
        }}>
            {options.map(o => (
                <button key={o} onClick={() => onChange(o)} style={{
                    padding: mini ? "4px 10px" : "6px 14px", borderRadius: mini ? 7 : 9, border: "none", cursor: "pointer",
                    fontFamily: FONT, fontSize: mini ? 12 : 13, fontWeight: value === o ? 700 : 600, whiteSpace: "nowrap",
                    background: value === o ? C.sage : "transparent", color: value === o ? "#FFF" : "rgba(22,25,37,.6)",
                }}>{o}</button>
            ))}
        </div>
    );

// ---------- chart conventions (Recharts) ----------
export const axisX = {
    tick: { fontSize: 11, fill: C.tick, fontFamily: FONT },
    axisLine: { stroke: C.base }, tickLine: false, tickMargin: 6,
};
export const axisY = {
    tick: { fontSize: 11, fill: C.faint, fontFamily: FONT },
    axisLine: false, tickLine: false, width: 34,
};
export const gridProps = { stroke: C.grid, vertical: false };
export const tipStyle: React.CSSProperties = {
    background: "#FFF", border: "none", borderRadius: 12, boxShadow: "0 8px 24px rgba(22,25,37,.14)",
    fontFamily: FONT, fontSize: 12, padding: "8px 12px", color: C.ink,
};
export const tipBar = {
    contentStyle: tipStyle, labelStyle: { color: C.muted, marginBottom: 4 },
    itemStyle: { padding: "1px 0" }, cursor: { fill: "rgba(22,25,37,.04)" },
};
export const tipLine = { ...tipBar, cursor: { stroke: "rgba(22,25,37,.2)", strokeWidth: 1 } };
export const barRadius: [number, number, number, number] = [4, 4, 0, 0];

// A tooltip body for custom content renderers.
export const TipBox: React.FC<{ title?: React.ReactNode; children: React.ReactNode }> = ({ title, children }) => (
    <div style={{ ...tipStyle, maxWidth: 280 }}>
        {title && <div style={{ color: C.muted, marginBottom: 4 }}>{title}</div>}
        {children}
    </div>
);

// ---------- tables ----------
export const th = (align: "left" | "right" | "center" = "left", active = false): React.CSSProperties => ({
    ...microLabel, color: active ? C.ink : C.tick, letterSpacing: "0.1em", textAlign: align,
    padding: "0 8px 10px", borderBottom: `1.5px solid ${C.ink}`, whiteSpace: "nowrap", fontWeight: 600,
});
export const td = (align: "left" | "right" | "center" = "left"): React.CSSProperties => ({
    fontFamily: FONT, fontSize: 13, padding: "9px 8px", borderBottom: `1px solid ${C.rowLine}`,
    textAlign: align, verticalAlign: "middle",
});
export const tdNum = (weight = 600, color?: string): React.CSSProperties => ({
    ...td("right"), ...numStyle(15, weight), color: color || C.ink, whiteSpace: "nowrap",
});
export const TableWrap: React.FC<{ minWidth: number; children: React.ReactNode }> = ({ minWidth, children }) => (
    <div style={{ overflowX: "auto", margin: "0 -4px" }}>
        <table style={{ borderCollapse: "collapse", width: "100%", minWidth, fontVariantNumeric: "tabular-nums" }}>{children}</table>
    </div>
);
